// The one place a role-to-route permission is written in the frontoffice. Two
// questions live here, kept apart because they are not the same question:
//
//   offeredTo — who the sidebar OFFERS an entry to. It reproduces the menu
//               table of caiet §4.2 (NC-MENU-01..17), which outranks the
//               wireframe menus wherever the two disagree.
//   openTo    — who may OPEN the address. It DEFAULTS to `offeredTo`, and is
//               written out only where the caiet grants an address the menu
//               does not offer.
//
// per docs/shape-ops-access-model.md §The model (owner ruling, 2026-09-05)
//
// Until today the guard read `visibleTo` off the menu, so the two were one
// field. `expiring` proves they are not: the wireframe gives the row to the
// observers alone, while the stocks screen — open to every role — draws every
// reader an unconditional link to it, and caiet UC91-01 grants every role the
// stock view at its own scope. One field cannot say "may open, is not offered",
// so the guard sent readers home from a link the application itself draws.
import { isObserverRole, type Role, type SessionUser } from '@simsm/shared-types';

// What access needs to know beyond the role code. Caiet UC92 and the owner's
// arbitration D1 make one entry depend on the *kind* of Registrar the user
// works for, which the role code alone cannot express.
export type NavContext = {
  // True when the ACTIVE Registrar is a Registrar de tip IMSP: the one being
  // worked on, not any the role holds (register UC11-05, CF-27). Callers that
  // cannot answer it omit the context and both readers fail closed.
  registrarIsImsp: boolean;
};

// The one kind code access cares about, kept next to the predicate that reads
// it. `registrarKinds` reaches the client verbatim from catalog.registrar
// (caiet UC10-02 resolves it at login), so the model asks its own boolean
// question of the session's list rather than storing an answer.
const IMSP_REGISTRAR_KIND = 'IMSP';

// Whether any Registrar of a role's scope is an IMSP, whichever one is active:
// the question a role's name asks (docs/shape-ops-role-label-imsp.md §2). The
// menu and the route guard ask the active Registrar's, below.
export function scopeHoldsImsp(registrarKinds: ReadonlyArray<string> | undefined): boolean {
  return registrarKinds?.includes(IMSP_REGISTRAR_KIND) === true;
}

/**
 * What the session alone says about the active Registrar's kind, or `undefined`
 * where only the bff can say (docs/shape-ops-tenant-selector.md §7.2).
 *
 * `registrarKinds` is the set of kinds of the role's Registrars, not one per
 * Registrar, so it answers only where it cannot be about another one: no active
 * Registrar, one outside the scope, or no IMSP among the kinds is not an IMSP; a
 * single Registrar with the IMSP kind is. Several Registrars with an IMSP among
 * their kinds is the bff's question, which `useNavContext` asks.
 */
export function navContextFor(
  scope: Pick<SessionUser, 'registrarScope' | 'registrarKinds'> | undefined,
  activeRegistrar: string,
): NavContext | undefined {
  const registrars: readonly string[] = scope?.registrarScope ?? [];
  const isImspKind = scopeHoldsImsp(scope?.registrarKinds);
  if (!activeRegistrar || !registrars.includes(activeRegistrar) || !isImspKind) {
    return { registrarIsImsp: false };
  }
  return registrars.length === 1 ? { registrarIsImsp: true } : undefined;
}

// Access is a predicate over the role code, never a literal list: Specific
// Observer roles are minted in MPass at run time (caiet UC61-02/03), so a code
// the frontend has never seen must still resolve to the observer navigation.
export type RoleMatcher = (role: Role, context?: NavContext) => boolean;

export const isSystemAdmin: RoleMatcher = (role) => role === 'SYSTEM_ADMIN';
export const isRegistrarAdmin: RoleMatcher = (role) => role === 'REGISTRAR_ADMIN';
export const isOperator: RoleMatcher = (role) => role === 'REGISTRAR_OPERATOR';
export const isObserver: RoleMatcher = (role) => isObserverRole(role);
export const isReporter: RoleMatcher = (role) => isRegistrarAdmin(role) || isOperator(role);

// caiet UC92-01 plus the roles table at §4.2 line 206: verificarea stocurilor
// interinstituționale belongs to the staff of a Registrator de tip IMSP,
// administrator and operator alike, and to the System Administrator. The
// wireframe gives it to the IMSP operator only, which drops the IMSP registrar
// administrator the caiet names; the arbitration in
// wireframes/00-decisions.md §Addendum 2026-08-28 D1 rules for the caiet.
//
// The kind lives on catalog.registrar and reaches the session through
// SessionUser.registrarKinds, resolved once at login per caiet UC10-02, and it
// is the ACTIVE Registrar's that decides, as it does in the bff (navContextFor).
// A caller that supplies no context still gets the entry hidden: showing a
// forbidden one advertises a function the BFF then refuses with
// NOT_IMSP_REGISTRAR. Fail closed.
const isImspReporter: RoleMatcher = (role, context) =>
  isReporter(role) && context?.registrarIsImsp === true;

const anyRole: RoleMatcher = () => true;
// An address the menu offers to no one: reached from a link on another screen.
const noRole: RoleMatcher = () => false;

const either =
  (...matchers: ReadonlyArray<RoleMatcher>): RoleMatcher =>
  (role, context) =>
    matchers.some((matches) => matches(role, context));

// A rule either says one thing — who is offered the entry, which is also who may
// open it — or it says both, and then it must say WHY they differ. The union is
// the enforcement: `openTo` without `grantedBy` does not compile. An exception
// whose caiet clause cannot be written is an exception that must not exist, and
// that is a rule the compiler can hold rather than one a reviewer has to.
//
// The `?: never` pair is what makes it hold. Excess-property checking against a
// bare union accepts any property declared by ANY member, so
// `{ offeredTo, openTo }` would satisfy the first branch and slip through
// uncited — while `mayOpen` reads `openTo` at RUN time and would widen access on
// it. Naming the absent fields closes that.
export type AccessRule =
  | { offeredTo: RoleMatcher; openTo?: never; grantedBy?: never }
  | { offeredTo: RoleMatcher; openTo: RoleMatcher; grantedBy: string };

// Keyed by nav target. Order is irrelevant here — the sidebar's order lives in
// NAV_ITEMS, where it is a drawing decision; resolution is by longest prefix.
export const ACCESS_RULES: Record<string, AccessRule> = {
  // caiet §4.2, menu table, first row: "Acasă — Toți utilizatorii" (Home, all
  // users). The System Administrator's Acasă is the reports register: the row is
  // offered and the address opens, and `/system-admin/home` forwards to
  // `/system-admin/reports` (owner decision Q-1-01; RoleHomeDispatcher), where
  // the wireframe redirected it too. The role has no separate home page.
  // per docs/shape-ops-system-admin-home.md
  //
  // Every role may open it, which is also what makes the guard total:
  // `/<role>/home` is the only destination a refusal produces, so a role that
  // could not open it would spin.
  home: { offeredTo: anyRole },

  reporting: { offeredTo: isReporter },
  // Raporturi nefinalizate — caiet §4.2 and the wireframe menu both give it to
  // both registrar roles.
  drafts: { offeredTo: isReporter },
  // Solicitări corectare dată expirare. The wireframe gives it to REG, which is
  // both registrar roles (wireframes/react/src/lib/menu.ts), and caiet §4.2 line
  // 202 lists "Administrator Registrator, Operator Registrator" for it; the role
  // table (line 134) makes the administrator "și calitatea de Operator pentru
  // toate locațiile Registratorului". UC51-01 names only the operator, but it
  // grants rather than restricts, so wireframe and caiet menu agree: both.
  corrections: { offeredTo: isReporter },

  reports: { offeredTo: either(isSystemAdmin, isReporter) },
  stocks: { offeredTo: anyRole },
  'stocks/verify': { offeredTo: either(isSystemAdmin, isImspReporter) },

  // The case this file was extracted for: an address every role may open that
  // the menu offers to no one. Caiet §4.2 has no row for expiring lots, and the
  // owner's reconciliation of 2026-10-12 (T033) rules it over wireframe parity
  // decision D7, which had kept the row for the Observers. The screen is reached
  // from the stock view, whose header draws the link for every role.
  // per docs/shape-ops-menu-alignment.md §Expiring lots: an address with no row
  //
  // The ADDRESS is open to every role, and three clauses say so together:
  // UC91-01 grants the stock view to all four actor kinds, each at its own scope
  // ("Administratorul Sistemului are acces la toate stocurile … Operatorul
  // Registratorului are acces la stocurile Registratorului pentru locațiile
  // asociate acestuia"); UC91-04 lists "Data expirării" among that list's own
  // columns, which is why the wireframe draws expiry as a column at all; and
  // UC-DASH-13 requires the screen by name, restricted to no role.
  //
  // The running system agrees (constitution V): GET /stock/expiring carries
  // SessionGuard alone and scopes its rows by scopeRole / scopeRegistrarIds /
  // scopeSiteIds, which is UC91-01 as code. This is also what makes the
  // unconditional link in StockChartTable honest: every role that can reach the
  // link can follow it.
  expiring: {
    offeredTo: noRole,
    openTo: anyRole,
    grantedBy:
      'caiet UC91-01 (the stock view, scoped per role, to all four actors) + UC91-04 ("Data expirării" is a column of that list) + UC-DASH-13 ("loturi expirate", named for no particular role)',
  },

  dashboards: { offeredTo: either(isSystemAdmin, isObserver) },

  medicines: { offeredTo: isSystemAdmin },
  geography: { offeredTo: isSystemAdmin },
  registrars: { offeredTo: isSystemAdmin },
  sites: { offeredTo: either(isSystemAdmin, isRegistrarAdmin) },
  credentials: { offeredTo: either(isSystemAdmin, isRegistrarAdmin) },
  approvals: { offeredTo: isSystemAdmin },
  aggregates: { offeredTo: isSystemAdmin },
  alerts: { offeredTo: isSystemAdmin },
  settings: { offeredTo: isSystemAdmin },
  'dashboards-admin': { offeredTo: isSystemAdmin },
};

// Map lookups, not property reads: a reader may type /operator/constructor, and
// a plain record would answer that with Object.prototype's own. The gate fails
// closed on a segment nobody declared.
const RULE_BY_TARGET = new Map(Object.entries(ACCESS_RULES));
const ACCESS_TARGETS = [...RULE_BY_TARGET.keys()];

// Route segments that belong to an entry they do not prefix. Two kinds, one
// table, because they are the same statement: this path belongs to that entry.
// No permission is decided here — the entry's own rule still decides.
//
// The first four are the alert configuration pages (the fourth, `watched-groups`, is the group the
// agreed alerts A4, A5 and A10 refer to), which live under their own
// segments but belong to the alerts tool. `inbox`, the address of the per-user
// alert inbox CF-46 retired, is NOT one of them: it has no route and no alias,
// so it is an unknown address like any other and answers with the reader's
// Acasă and the guard's notice (owner decision Q-1-07, BUG-1-032).
// `expiring` has a rule of its own, which the guard reaches first because its
// candidates are every rule; the alias is read only by the sidebar, whose
// candidates are the drawn rows, so the expiry page keeps "Vizualizare stocuri"
// lit, the row whose screen links to it (docs/shape-ops-menu-alignment.md).
// The last four reach no entry at all:
// `grafana` is a redirect to `dashboards`, so it is the same screen under a
// retired address, and the other three are tools whose menu row went away when
// the wireframe folded their function into another screen, and whose route
// outlives the row. Each destination is the one router.tsx names in its own
// comment. When a route is finally retired, its line here goes with it.
export const SEGMENT_ALIASES: Record<string, string> = {
  'alert-templates': 'alerts',
  'alert-policies': 'alerts',
  'alert-channels': 'alerts',
  'watched-groups': 'alerts',
  expiring: 'stocks',
  grafana: 'dashboards',
  'mconnect-credentials': 'settings',
  'mconnect-events': 'registrars',
  'observer-roles': 'dashboards-admin',
};

const ALIAS_BY_SEGMENT = new Map(Object.entries(SEGMENT_ALIASES));

// Longest-prefix match against the targets so 'stocks/verify' resolves to its
// own entry instead of 'stocks', and nested routes (medicines/import) still
// resolve to their parent. ONE resolver: the row that lights up in the sidebar
// and the permission the guard checks are the same computation, which is what
// keeps the two from drifting.
export function activeSegment(pathname: string, role: string, targets: string[]): string {
  const prefix = `/${role}/`;
  if (!pathname.startsWith(prefix)) return 'home';
  const rest = pathname.slice(prefix.length);
  let best = '';
  for (const target of targets) {
    if ((rest === target || rest.startsWith(`${target}/`)) && target.length > best.length) {
      best = target;
    }
  }
  if (best) return best;
  const seg = rest.split('/')[0] ?? 'home';
  return ALIAS_BY_SEGMENT.get(seg) ?? (seg || 'home');
}

// The entry a route belongs to, or null when it belongs to none — in which case
// no role opens it, and a route added with no owner is unreachable rather than
// quietly public.
//
// The candidate list is EVERY target, never the role's visible ones: given only
// what the role can see, a non-IMSP operator's `stocks/verify` would fall back
// onto `stocks`, which every role may open.
export function accessTargetFor(pathname: string, roleSegment: string): string | null {
  const resolved = activeSegment(pathname, roleSegment, ACCESS_TARGETS);
  return RULE_BY_TARGET.has(resolved) ? resolved : null;
}

// Who the sidebar offers the entry to.
export function isOfferedTo(target: string, role: Role, context?: NavContext): boolean {
  const rule = RULE_BY_TARGET.get(target);
  return rule !== undefined && rule.offeredTo(role, context);
}

// Who may open the address. The default is the menu; only a cited exception
// widens it.
export function mayOpen(target: string, role: Role, context?: NavContext): boolean {
  const rule = RULE_BY_TARGET.get(target);
  if (rule === undefined) return false;
  return 'openTo' in rule ? rule.openTo(role, context) : rule.offeredTo(role, context);
}
